import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { CartItem, AddToCartInput } from '../model/types'
import { CART_STORAGE_KEY } from '../config/cart.config'

interface CartState {
  items: CartItem[]
  drawerOpen: boolean
  addItem: (input: AddToCartInput) => void
  removeItem: (id: string) => void
  setQty: (id: string, qty: number) => void
  increment: (id: string) => void
  decrement: (id: string) => void
  clear: () => void
  openDrawer: () => void
  closeDrawer: () => void
  toggleDrawer: () => void
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      drawerOpen: false,

      addItem: (input) => {
        const qty = input.qty ?? 1
        const existing = get().items.find((it) => it.id === input.id)
        if (existing) {
          set({
            items: get().items.map((it) =>
              it.id === input.id ? { ...it, qty: it.qty + qty } : it,
            ),
            drawerOpen: true,
          })
        } else {
          const next: CartItem = {
            id: input.id,
            slug: input.slug,
            name: input.name,
            brand: input.brand,
            image: input.image,
            price: input.price,
            oldPrice: input.oldPrice,
            qty,
          }
          set({ items: [...get().items, next], drawerOpen: true })
        }
      },

      removeItem: (id) =>
        set({ items: get().items.filter((it) => it.id !== id) }),

      setQty: (id, qty) => {
        const n = Math.max(1, Math.min(99, Math.floor(qty)))
        set({
          items: get().items.map((it) => (it.id === id ? { ...it, qty: n } : it)),
        })
      },

      increment: (id) =>
        set({
          items: get().items.map((it) =>
            it.id === id ? { ...it, qty: Math.min(99, it.qty + 1) } : it,
          ),
        }),

      decrement: (id) =>
        set({
          items: get().items.map((it) =>
            it.id === id ? { ...it, qty: Math.max(1, it.qty - 1) } : it,
          ),
        }),

      clear: () => set({ items: [] }),

      openDrawer: () => set({ drawerOpen: true }),
      closeDrawer: () => set({ drawerOpen: false }),
      toggleDrawer: () => set({ drawerOpen: !get().drawerOpen }),
    }),
    {
      name: CART_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items }),
    },
  ),
)
